import axios from "axios";
import auth from "./authHeaders";
import { getApiBaseUrl } from "./apiConfig";

const buildHeaders = (isMultipart = false) => ({
  "Content-Type": isMultipart ? "multipart/form-data" : "application/json",
  ...auth(),
});

const createClient = (isMultipart = false) =>
  axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 30000,
    headers: buildHeaders(isMultipart),
  });

let client = createClient();

export function refreshApiClient(isMultipart = false) {
  client = createClient(isMultipart);
}

const setMultipart = (isMultipart = false) => {
  refreshApiClient(isMultipart);
};

const translatorClient = axios.create({
  baseURL: "https://agents.affordamic.com",
  headers: {
    "Content-Type": "application/json",
  },
});

const getUserRole = () => {
  return localStorage.getItem("user_role");
};
const login = (data) => {
  return client.post("auth/emp-login", data, { headers: buildHeaders() });
};
const verifyTwoFactorLogin = (data) => {
  return client.post("auth/verify-2fa", data, { headers: buildHeaders() });
};
const getSecurityStatus = () => {
  return client.get("auth/security/status");
};
const changePassword = (data) => {
  setMultipart(false);
  return client.post("auth/security/change-password", data);
};
const setupTwoFactor = () => {
  setMultipart(false);
  return client.post("auth/security/2fa/setup", {});
};
const enableTwoFactor = (data) => {
  setMultipart(false);
  return client.post("auth/security/2fa/enable", data);
};
const disableTwoFactor = (data) => {
  setMultipart(false);
  return client.post("auth/security/2fa/disable", data);
};
const getAllTestSeries = (params) => {
  return client.get("series/admin/getAll", { params });
};
const getArrayByName = (name) => {
  return client.get("common/array/getByName", { params: { name } });
};
const addArrayByName = (data) => {
  return client.post("common/array/add", data);
};
const getAllRewardConditions = (params) => {
  return client.get("rewards/getAll", { params });
};
const addSeries = (data) => {
  return client.post("series/addSeries", data);
};
const updateSeries = (data) => {
  return client.post("series/update", data);
};
const deleteSeries = (id) => {
  return client.get("series/deleteTestSeries", { params: { seriesId: id } });
};
const getSeriesDetails = (seriesId) => {
  return client.get("series/get/details", { params: { seriesId } });
};
const uploadSeriesImages = (seriesId, key, file) => {
  const form = new FormData();
  form.append("seriesId", seriesId);
  form.append(key, file);
  return client.post("series/add-images", form);
};
const addTest = (data) => {
  return client.post("series/exam/add", data);
};
const updateTest = (data) => {
  return client.post("series/exam/update", data);
};
const deleteTest = (id) => {
  return client.get("series/exam/delete", { params: { testId: id } });
};
const getAllTests = (params) => {
  return client.get("series/admin/exam/getAll", { params });
};
const getTestDetails = (id) => {
  return client.get("series/exam/details", { params: { testId: id } });
};
const getAllStaticContents = (params) => {
  return client.get("static/content/getAll", { params });
};
const addStaticContent = (data) => {
  return client.post("static/create/content", data);
};
const viewStaticContent = (id) => {
  return client.get("static/content/get", { params: { contentId: id } });
};
const editStaticContent = (data) => {
  return client.post("static/update/content", data);
};
const deleteStaticContent = (id) => {
  return client.get("static/delete/content", { params: { contentId: id } });
};
const uploadImage = (quesCode, name, key, file) => {
  setMultipart(true);
  let form = new FormData();
  form.append(key, file);
  form.append("questionCode", quesCode);
  form.append("name", name);
  return client.post("ques/getImageUrl", form);
};
const AddQuestion = (data) => {
  setMultipart(false);
  return client.post("/ques/add-new", data);
};
const AddQuestionVarient = (data) => {
  setMultipart(false);
  return client.post("/ques/add-varient", data);
};
const getQuesVarients = (id) => {
  setMultipart(false);
  return client.get("/ques/get-varients", { params: { questionId: id } });
};
const getAllQuestions = (data, options = {}) => {
  setMultipart(false);
  return client.get("ques/getAll", { params: data, signal: options.signal });
};
const getAllQuestionPivots = (data) => {
  setMultipart(false);
  return client.get("ques/getQuestionPivots", { params: data });
};
const getQuestionDetails = (id, lang) => {
  return client.get("ques/get/details", {
    params: { id, code: id, lang: lang },
  });
};

const runQuestionValidation = (questionId, lang, provider) => {
  setMultipart(false);
  return client.post(`ques/validation/${questionId}/run`, { lang: lang || "en", provider });
};

const markValidationIssueResolved = (questionId, field, lang) => {
  setMultipart(false);
  return client.post(`ques/validation/${questionId}/resolve`, { field, lang: lang || "en" });
};

const runQuestionValidationBatch = (questionIds, lang, provider) => {
  setMultipart(false);
  return client.post("ques/validation/batch", {
    questionIds,
    lang: lang || "en",
    provider,
  });
};

const getQuestionMappings = (id) => {
  return client.get("ques/getMapping", { params: { id } });
};
const updateQuestion = (data) => {
  setMultipart(false);
  return client.post("ques/update", data);
};
const deleteQuestion = (params) => {
  setMultipart(false);
  return client.get("ques/delete", { params });
};
const getAllFeeders = (data) => {
  setMultipart(false);
  return client.get("cfeeders/getAll", { params: data });
};
const getAllValidators = (data) => {
  setMultipart(false);
  return client.get("cvalidators/getAll", { params: data });
};
const addFeeder = (data) => {
  setMultipart(false);
  return client.post("cfeeders/add", data);
};
const updateFeederPassword = (id) => {
  setMultipart(false);
  return client.get("cfeeders/change-password", { params: { userId: id } });
};
const deleteFeeder = (id) => {
  setMultipart(false);
  return client.get("cfeeders/delete", { params: { userId: id } });
};
const updateFeederAccount = (data) => {
  setMultipart(false);
  return client.post("cfeeders/update", data);
};
const changeQuesVerify = (id) => {
  setMultipart(false);
  return client.get("ques/verify-question", { params: { id } });
};
const getEarningStats = (userId) => {
  setMultipart(false);
  return client.get("cfeeders/getStats", { params: { userId } });
};
const claimEarning = (userId) => {
  setMultipart(false);
  return client.get("cfeeders/requestClaim", { params: { userId } });
};
const getClaimRequests = (data) => {
  setMultipart(false);
  return client.get("cfeeders/getClaimRequests", { params: data });
};
const updateClaimRequest = (data) => {
  setMultipart(false);
  return client.post("cfeeders/updateClaimRequest", data);
};
const addValidator = (data) => {
  setMultipart(false);
  return client.post("cvalidators/add", data);
};
const updateValidatorPassword = (id) => {
  setMultipart(false);
  return client.get("cvalidators/change-password", { params: { userId: id } });
};
const deleteValidator = (id) => {
  setMultipart(false);
  return client.get("cvalidators/delete", { params: { userId: id } });
};
const updateValidatorAccount = (data) => {
  setMultipart(false);
  return client.post("cvalidators/update", data);
};
const getAllValidatorQuestions = (data) => {
  setMultipart(false);
  return client.get("cvalidators/getQuestions", { params: data });
};
const getValidatorAccountStats = (data) => {
  setMultipart(false);
  return client.get("cvalidators/getStats", { params: data });
};
const shiftQuestions = (data) => {
  setMultipart(false);
  return client.post("ques/shift", data);
};
const getTestQuestions = (data) => {
  setMultipart(false);
  return client.get("ques/testQuestions", { params: data });
};
const searchQuestions = (data) => {
  setMultipart(false);
  return client.get("ques/search", { params: data });
};
const testQuesMapping = (data) => {
  setMultipart(false);
  return client.post("series/exam/question-mapping", data);
};
const removeQuestionMapping = (data) => {
  setMultipart(false);
  return client.post("series/remove/question-mapping", data);
};
const addTestSection = (data) => {
  setMultipart(false);
  return client.post("series/exam/add-section", data);
};
const removeTestSection = (data) => {
  setMultipart(false);
  return client.post("series/exam/remove-section", data);
};
const getMappedTests = (data) => {
  setMultipart(false);
  return client.get("series/get/series-all-tests", {
    params: { seriesId: data },
  });
};
const addTestToSeries = (data) => {
  setMultipart(false);
  return client.post("series/add-test-to-series", data);
};
const updateTestsToSeries = (data) => {
  setMultipart(false);
  return client.post("series/update-tests-in-series", data);
};
const getAllStories = (query) => {
  setMultipart(false);
  return client.get("story/getAll", { params: query });
};
const deleteStory = (id) => {
  setMultipart(false);
  return client.get("story/delete", { params: { storyId: id } });
};
const toggleStoryStatus = ({ id, status }) => {
  setMultipart(false);
  return client.get("story/status/update", {
    params: { storyId: id, status: status },
  });
};
const addStory = (data) => {
  setMultipart(true);
  return client.post("story/release", data);
};
const getAdminStats = () => {
  setMultipart(false);
  return client.get("admin/statatics", { params: {} });
};
const getUsers = (params) => {
  setMultipart(false);
  return client.get("user/getAll", { params: params });
};
const getUserDetail = (id) => {
  setMultipart(false);
  return client.get("user/get/detail", { params: { userId: id } });
};
const getEmployees = (params) => {
  setMultipart(false);
  return client.get("admin/get/employees", { params });
};
const addEmployee = (data) => {
  setMultipart(false);
  return client.post("admin/add-employee", data);
};
const getEmployeeDetails = (employeeId) => {
  setMultipart(false);
  return client.get("admin/get/employee", { params: { employeeId } });
};
const updateEmployee = (data) => {
  setMultipart(false);
  return client.put("admin/update-employee", data);
};
const toggleEmployeeStatus = (params) => {
  setMultipart(false);
  return client.get("admin/change-emp-status", { params });
};
const deleteEmployee = (employeeId) => {
  setMultipart(false);
  return client.get("admin/delete-employee", { params: { employeeId } });
};
const getUserAssistantSessions = (userId, params) => {
  setMultipart(false);
  return client.get(`admin/users/${userId}/assistant/sessions`, { params });
};
const getUserWallet = (id) => {
  return client.get("user/wallet/getBalance", { params: { userId: id } });
};
const getUserOrders = (params) => {
  setMultipart(false);
  return client.get("purchase/order/getAll", { params: params });
};
const getUserOrdersDetails = (orderId) => {
  setMultipart(false);
  return client.get("purchase/order/detail", { params: { orderId } });
};
const getUserLoginActivity = (id, offset) => {
  setMultipart(false);
  return client.get("user/recent-logins", { params: { userId: id, offset } });
};
const updateUserWallet = (data) => {
  setMultipart(false);
  return client.post("admin/user/wallet/update", data);
};
const createUserWallet = (id) => {
  setMultipart(false);
  return client.get("user/wallet/create", { params: { userId: id } });
};
const initRefund = (data) => {
  setMultipart(false);
  return client.get("/purchase/order/cancel", { params: data });
};
const updateUser = (data) => {
  setMultipart(false);
  return client.post("/user/update/profile", data);
};
const verifyExplanation = (id) => {
  setMultipart(false);
  return client.get("ques/verify-explanation", { params: { id } });
};
const getTestReport = (id, trackerId) => {
  return client.get("series/exam/report", {
    params: { testId: id, trackerId: trackerId },
  });
};

const getSectionQuestion = (data) => {
  return client.post("series/exam/get-ques", data);
};

const getTestTracker = (id) => {
  return client.get("/series/exam/get/tracker", { params: { testId: id } });
};
const updateQuestionState = (
  trackerId,
  quesId,
  state,
  answerId,
  durationInSec
) => {
  return client.get("/series/exam/update/testQuesState", {
    params: {
      trackerId: trackerId,
      quesId: quesId,
      state: state,
      answerId: answerId,
      durationInSec,
    },
  });
};

const getinitializeTest = (id, defaultLang, isTnCAaccepted) => {
  return client.get("/series/exam/initTest", {
    params: {
      testId: id,
      defaultLang: defaultLang,
      isTnCAaccepted: isTnCAaccepted,
    },
  });
};

const getTestSolutions = (params) => {
  return client.get("/series/exam/solutions", { params: params });
};

const submitTest = (data) => {
  const payload =
    typeof data === "string" || typeof data === "number"
      ? { trackerId: data }
      : data;
  return client.post("/series/exam/submit", payload);
};

const getAllContentManagers = (params) => {
  setMultipart(false);
  return client.get("/user/cm/getAll", { params: params });
};

const getContentManagerStats = () => {
  setMultipart(false);
  return client.get("/user/cm/get/stats");
};

const getContentManagerDetails = (id) => {
  setMultipart(false);
  return client.get("/user/cm/get-profile", { params: { userId: id } });
};
const deleteContentManager = (id) => {
  setMultipart(false);
  return client.get("/user/cm/delete", { params: { userId: id } });
};
const updateContentManagerPassword = (id) => {
  setMultipart(false);
  return client.get("user/cm/change-password", { params: { userId: id } });
};
const addContentManager = (data) => {
  setMultipart(false);
  return client.post("/user/cm/add", data);
};
const updateContentManager = (data) => {
  setMultipart(false);
  return client.post("/user/cm/update", data);
};
const getAllContentManagerWorkTasks = (params) => {
  setMultipart(false);
  return client.get("/user/cm/getAll/work-task", { params: params });
};
const updateContentManagerRole = (data) => {
  setMultipart(false);
  return client.post("/user/cm/update-access-role", data);
};
const addContentManagerWorkTask = (data) => {
  setMultipart(false);
  return client.post("/user/cm/add/work-task", data);
};
const updateWorkTaskStatus = (params) => {
  setMultipart(false);
  return client.get("/user/cm/update/work-task-status", { params: params });
};

const getTopicWiseReport = (params) => {
  return client.get("/series/exam/report/topic-wise", { params: params });
};

const getQuestionSolutionDetails = (params) => {
  return client.get("series/exam/report/question-details", { params: params });
};

const getWorkTaskDetails = (id) => {
  setMultipart(false);
  return client.get("/user/cm/get/work-task", { params: { workTaskId: id } });
};

const deleteWorkTask = (id) => {
  setMultipart(false);
  return client.get("/user/cm/delete/work-task", {
    params: { workTaskId: id },
  });
};

const updateWorkTask = (data) => {
  setMultipart(false);
  return client.post("/user/cm/edit/work-task", data);
};

const getAllTickets = (params) => {
  setMultipart(false);
  return client.get("/admin/i/complaints/get-all", { params: params });
};

const getTicketDetails = (id) => {
  setMultipart(false);
  return client.get("/admin/i/complaints/ticket-details", {
    params: { complaintId: id },
  });
};

const performTicketAction = (data) => {
  setMultipart(true);
  return client.post("/admin/i/complaints/perform-action", data);
};

const updateTicketStatus = (params) => {
  setMultipart(false);
  return client.get("/admin/i/complaints/update/ticket-status", {
    params: params,
  });
};

const getAllClaimRequests = (params) => {
  setMultipart(false);
  return client.get("/user/cm/get/claim-requests", { params: params });
};

const getClaimRequestStats = (params) => {
  setMultipart(false);
  return client.get("/user/cm/get/claim-request-stats", { params });
};

const getClaimRequestDetails = (id) => {
  setMultipart(false);
  return client.get("/user/cm/get/claim-request-detail", {
    params: { requestId: id },
  });
};

const updateClaimRequestStatus = (data) => {
  setMultipart(false);
  return client.post("/user/cm/update/claim-request-status", data);
};

const getCMClaimHistory = (params) => {
  setMultipart(false);
  return client.get("/user/cm/get/claim-history", { params: params });
};

const getCMTasks = (params) => {
  setMultipart(false);
  return client.get("/user/cm/client/all-work-task", { params: params });
};

const CMAcceptRejectTask = (params) => {
  setMultipart(false);
  return client.get("/user/cm/client/task/acceptOrReject", { params: params });
};

const getCMClaimableTaskList = () => {
  setMultipart(false);
  return client.get("/user/cm/get/claimable-tasks");
};

const getCMDashboardStatus = () => {
  setMultipart(false);
  return client.get("/user/cm/client/dashboard-stats");
};

const requestCMNewClaim = (data) => {
  setMultipart(false);
  return client.post("/user/cm/requestClaim", data);
};

const getEarningDashboardDetails = (id) => {
  setMultipart(false);
  return client.get("/user/cm/get/earning-stats", { params: { userId: id } });
};

const getCMFandQ = (params) => {
  setMultipart(false);
  return client.get("/admin/i/complaints/get-all", { params: params });
};

const addCMTicket = (data) => {
  setMultipart(true);
  return client.post("/admin/i/complaints/create-ticket", data);
};

const getCMTaskDetails = (params) => {
  setMultipart(false);
  return client.get("/user/cm/client/work-task", { params: params });
};

const validateCMQuestionUnit = (data) => {
  setMultipart(false);
  return client.post("/user/cm/client/task/validate-unit", data);
};

const updateQuestionStatusLevelWise = (data) => {
  setMultipart(false);
  return client.post("/ques/update/QuestionVerifyStatus", data);
};

const getAllDropdowns = () => {
  setMultipart(false);
  return client.get("/common/array/getAll");
};

const deleteArrayByName = (params) => {
  setMultipart(false);
  return client.get("/common/array/remove", { params: params });
};

const addArray = (data) => {
  setMultipart(false);
  return client.post("/common/array/add-multiple", data);
};

const updateArray = (data) => {
  setMultipart(false);
  return client.post("/common/array/update", data);
};

const getAllTestConfigs = (params) => {
  setMultipart(false);
  return client.get("/admin/test-config/getAll", { params: params });
};

const addTestConfig = (data) => {
  setMultipart(false);
  return client.post("/admin/create/test-config", data);
};

const editTestConfig = (data) => {
  setMultipart(false);
  return client.post("/admin/update/test-config", data);
};

const deleteTestConfig = (id) => {
  setMultipart(false);
  return client.get("/admin/delete/test-config", { params: { configId: id } });
};

const getTestConfigDetails = (id) => {
  setMultipart(false);
  return client.get("/admin/get/test-config", { params: { configId: id } });
};

const updateStory = (data) => {
  setMultipart(true);
  return client.post("/story/update", data);
};

const changeTrackerLanguage = (data) => {
  setMultipart(false);
  return client.post("/series/exam/update-tracker-lang", data);
};

const getLineChartData = (params) => {
  setMultipart(false);
  return client.get("/series/exam/report/graphs", { params: params });
};

const getLeadershipBoard = (id) => {
  setMultipart(false);
  return client.get("/series/exam/leadershipboard", { params: { testId: id } });
};

const getAllTestTrackers = (id) => {
  setMultipart(false);
  return client.get("/series/exam/get/all-trackers", {
    params: { testId: id },
  });
};

const getAllExamsNotifications = (params) => {
  return client.get("/web/all/eb-notifications", { params: params });
};

const getExamNotificationsDetails = (id) => {
  return client.get("/web/get/eb-notification", {
    params: { notificationId: id },
  });
};

const getAllEntities = (params) => {
  return client.get("/web/get/entity-list-all", { params });
};

const getControlBoardList = (params) => {
  return client.get("/admin/control-board", { params });
};

const updateControlBoardStatus = (id, isEnabled) => {
  return client.patch(`/admin/control-board/${id}/status`, { isEnabled });
};

const updateControlBoardYears = (id, enabledYears) => {
  return client.put(`/admin/control-board/${id}/years`, { enabledYears });
};

const addExamNotification = (data) => {
  setMultipart(true);
  return client.post("/web/create/eb-notification", data);
};

const updateExamNotification = (data) => {
  setMultipart(true);
  return client.post("/web/update/eb-notification", data);
};

const deleteExamNotification = (id) => {
  return client.get("/web/delete/eb-notification", {
    params: { notificationId: id },
  });
};

const updateExamNotificationStatus = (data) => {
  setMultipart(false);
  return client.post("/web/update/eb-notification/status", data);
};

const addEntity = (data) => {
  setMultipart(false);
  return client.post("/web/add/entity", data);
};

const updateEntity = (data) => {
  setMultipart(false);
  return client.post("/web/update/entity", data);
};

const deleteEntity = (id) => {
  return client.get("/web/delete/entityListItem", { params: { id } });
};

const fillEntityContent = (data) => {
  setMultipart(true);
  return client.post("/web/update/entity-details", data);
};

const updateEntityBanners = (data) => {
  setMultipart(true);
  return client.post("/web/update/entity-banners", data);
};

const getEntityDetails = (id) => {
  return client.get("/web/get/entity-details", {
    params: { id },
  });
};

const deleteEntityBanner = (params) => {
  return client.get("/web/remove/entity-banners", {
    params: params,
  });
};

const uploadAttachment = (data) => {
  setMultipart(true);
  return client.post("/chat/forum/chat/files/upload", data);
};

const getChatForums = (params) => {
  setMultipart(false);
  return client.get("/web/get/forums", { params });
};

const getChatForumByExam = (examId) => {
  setMultipart(false);
  return client.get("/web/get/forum", { params: { id: examId } });
};

const createChatForum = (data) => {
  setMultipart(false);
  return client.post("/web/create/forum", data);
};

const getChatForumDetails = (tagId) => {
  setMultipart(false);
  return client.get("/web/hashtag/details", { params: { tagId } });
};

const deleteChatForumHashtag = (data) => {
  setMultipart(false);
  return client.post("/web/hashtag/delete", data);
};

const updateChatForumHashtagStatus = (data) => {
  setMultipart(false);
  return client.post("/web/hashtag/status", data);
};

const getAdminTransactionStats = (params) => {
  return client.get("/admin/transaction-stats", { params });
};

const getAdminUsersGrowth = () => {
  return client.get("/admin/user-growth");
};

const getEntitiesLists = (params) => {
  return client.get("/admin/exam-entities", { params });
};

const addListsEntity = (data) => {
  setMultipart(false);
  return client.post("/admin/exam-entities", data);
};

const updateListsEntity = (data) => {
  setMultipart(false);
  return client.put("/admin/exam-entities", data);
};

const deleteListsEntity = (id) => {
  return client.delete(`/admin/exam-entities/${id}`);
};

const getExamsLists = (params) => {
  return client.get("/admin/exams", { params });
};

const addListsExam = (data) => {
  setMultipart(false);
  return client.post("/admin/exams", data);
};

const updateListsExam = (data) => {
  setMultipart(false);
  return client.put("/admin/exams", data);
};

const deleteListsExam = (id) => {
  return client.delete(`/admin/exams/${id}`);
};

const getMappingsSubjects = (params) => {
  return client.get("/admin/subjects", { params });
};

const addMappingsSubject = (data) => {
  setMultipart(false);
  return client.post("/admin/subjects", data);
};

const getMappingsSubject = (id) => {
  return client.get(`/admin/subjects/id/${id}`);
};

const deleteMappingsSubject = (id) => {
  return client.delete(`/admin/subjects/id/${id}`);
};

const updateMappingsSubject = (data) => {
  setMultipart(false);
  return client.put("/admin/subjects", data);
};

const getAllSubjectsNames = (params) => {
  return client.get("/admin/subjects/all-names", { params });
};

const getAllCompetitions = (params) => {
  return client.get("/admin/competition/all", { params });
};

const addCompetition = (data) => {
  setMultipart(false);
  return client.post("/admin/competition/create", data);
};

const getCompetitionDetails = (id) => {
  return client.get("/admin/competition/details", {
    params: { id },
  });
};

const updateCompetitionStatus = (data) => {
  return client.put("/admin/competition/update-status", data);
};

const updateCompetition = (data) => {
  setMultipart(false);
  return client.put("/admin/competition/update", data);
};

const getCompetitionParticipants = (id) => {
  return client.get("/admin/competition/participants", {
    params: { competitionId: id },
  });
};

const getCompetitionScheduledTransitions = (id) => {
  return client.get("/admin/competition/scheduled-transitions", {
    params: { id },
  });
};

const getCompetitionProcessingStatus = (id) => {
  return client.get("/admin/competition/processing-status", {
    params: { id },
  });
};

const reprocessCompetitionResults = (id) => {
  return client.post("/admin/competition/reprocess-results", { id });
};

const getCompetitionLeaderboard = (competitionId, params = {}) => {
  return client.get("/admin/competition/leaderboard", {
    params: { competitionId, ...params },
  });
};

const getUserWalletHistory = (params) => {
  const { pageIndex, ...rest } = params;
  return client.get("/user/wallet/history", {
    params: {
      ...rest,
      offset: pageIndex != null ? pageIndex - 1 : rest.offset ?? 0,
    },
  });
};

const getUserActivities = (userId, params) => {
  return client.get(`/admin/users/${userId}/activities`, { params });
};

const getUserWalletIntegrity = (userId) => {
  return client.get(`/admin/users/${userId}/wallet/integrity`);
};

const getPaymentTimeline = (userId, orderId) => {
  return client.get(`/admin/users/${userId}/payments/${orderId}/timeline`);
};

const resolveWalletIntegrity = (userId, data) => {
  setMultipart(false);
  return client.post(`/admin/users/${userId}/wallet/resolve-integrity`, data);
};

const nllbTranslation = (data) => {
  setMultipart(false);
  return translatorClient.post("/translate/nllb", data);
};

const indicTranslation = (data) => {
  setMultipart(false);
  return translatorClient.post("/translate/indic", data);
};

const getStudyMaterials = (params) => {
  return client.get("/staticmaterial/content", { params });
};

const addStudyMaterial = (data) => {
  setMultipart(true);
  return client.post("/staticmaterial/content/add", data);
};

const updateStudyMaterial = (data) => {
  setMultipart(true);
  return client.put("/staticmaterial/content", data);
};

const updateStudyMaterialStatus = (data) => {
  setMultipart(false);
  return client.post("/staticmaterial/content/visibility", data);
};

const deleteStudyMaterial = (id) => {
  return client.get("/staticmaterial/content/delete", {
    params: { contentId: id },
  });
};

const getSubjectTopics = (params) => {
  return client.get("/admin/subjects/topics", { params });
};

const addQuestionVarientAuto = (data) => {
  setMultipart(false);
  // LLM translation (especially chunked long explanations) can exceed the default 30s client timeout.
  return client.post("/ques/add-varient-auto", data, { timeout: 300000 });
};

const updateUserWalletStatus = (data) => {
  setMultipart(false);
  return client.post("/user/wallet/toggleStatus", data);
};

const updateUserWalletBalance = (data) => {
  setMultipart(false);
  return client.post("/user/wallet/adjustBalance", data);
};

const sendVerificationEmail = (data) => {
  setMultipart(false);
  return client.post("/user/send-verification-email", data);
};

const getUserAvatarsList = (id) => {
  return client.get("/user/get/avatars", {
    params: { userId: id },
  });
};

const getOffers = (params) => {
  return client.get("/admin/promo-codes", { params });
};

const addOffer = (data) => {
  setMultipart(false);
  return client.post("/admin/promo-codes", data);
};

const updateOffer = (data) => {
  setMultipart(false);
  return client.post("/admin/update/promo-codes", data);
};

const getFaqs = (params) => {
  return client.get("/faq/public/list", { params });
};

const getFaqStats = () => {
  return client.get("/faq/public/stats");
};

const addFaq = (data) => {
  setMultipart(false);
  return client.post("/faq/create", data);
};

const updateFaq = (data) => {
  setMultipart(false);
  return client.put(`/faq/update/${data.id}`, data);
};

const getFaqDetails = (id) => {
  return client.get(`/faq/public/details/${id}`);
};

const deleteFaq = (id) => {
  return client.delete(`/faq/delete/${id}`);
};

const addQuestionToFaq = (data) => {
  setMultipart(false);
  return client.post(`/faq/add-multiple/${data.id}`, data);
};

const updateQuestionToFaq = (data) => {
  setMultipart(false);
  return client.put(`/faq/update-specific/${data.id}/${data.index}`, data);
};

const deleteQuestionToFaq = (id, index) => {
  setMultipart(false);
  return client.delete(`/faq/delete-specific/${id}/${index}`);
};

const reorderQuestions = (data) => {
  setMultipart(false);
  return client.post(`/faq/reorder/${data.id}`, data);
};

const getUserIssues = (params) => {
  return client.get("/user/issue/admin/all", { params });
};

const getUserIssueStats = () => {
  return client.get("/user/issue/admin/stats");
};

const getUserIssueDetails = (id, userId) => {
  return client.get(`/user/issue/issues/${id}`, { params: { userId } });
};

const performInternalIssueAction = (id, data) => {
  setMultipart(true);
  return client.post(`/user/issue/admin/${id}/actions`, data);
};

const updateUserIssue = (id, data) => {
  return client.put(`/user/issue/admin/${id}`, data);
};

const getQuestionBankStats = () => {
  return client.get("/ques/stats");
};

const getAllSubjects = (params) => {
  return client.get("/admin/subjects-main", { params });
};

const addSubject = (data) => {
  setMultipart(false);
  return client.post("/admin/subjects-main", data);
};

const updateSubject = (data) => {
  setMultipart(false);
  return client.put("/admin/subjects-main", data);
};

const deleteSubject = (id) => {
  return client.delete(`/admin/subjects-main/${id}`);
};

const getAllTopics = (params) => {
  return client.get("/admin/topics-main", { params });
};

const addTopic = (data) => {
  setMultipart(false);
  return client.post("/admin/topics-main", data);
};

const updateTopic = (data) => {
  setMultipart(false);
  return client.put("/admin/topics-main", data);
};

const deleteTopic = (id) => {
  return client.delete(`/admin/topics-main/${id}`);
};

const getAllCountries = () => {
  return client.get("/admin/countries");
};

const getAllStates = () => {
  return client.get("/admin/states");
};

const getAllIssuesCategories = () => {
  return client.get("/user/issue/categories");
};

const generateSelfHostedExplanation = (data) => {
  setMultipart(false);
  return client.post("/ques/generate-explanation", data);
};

const generateAIMLExplanation = (data) => {
  setMultipart(false);
  return client.post("/ques/generate-explanation-aiml", data);
};

const generateShortExplanation = (data) => {
  setMultipart(false);
  return client.post("/ques/generate-explanation-short", data);
};

const getUsersNotifications = (params) => {
  return client.get("/notifications", { params });
};

const addUsersNotification = (data) => {
  setMultipart(false);
  return client.post("/notifications", data);
};

const updateUsersNotification = (data) => {
  setMultipart(false);
  return client.put(`/notifications/${data.id}`, data);
};

const deleteUsersNotification = (id) => {
  return client.delete(`/notifications/${id}`);
};

const sendUsersNotification = (id) => {
  setMultipart(false);
  return client.post(`/notifications/${id}/send`);
};

const createMockTestCreatorJob = (data) => {
  setMultipart(false);
  return client.post("/admin/mock-test-creator/jobs", data);
};

const listMockTestCreatorJobs = (params) => {
  return client.get("/admin/mock-test-creator/jobs", { params });
};

const getMockTestCreatorJob = (jobId) => {
  return client.get(`/admin/mock-test-creator/jobs/${jobId}`);
};

const previewMockReferenceTest = (testId) => {
  return client.get(`/admin/mock-test-creator/reference-tests/${testId}/preview`);
};

const previewMockReferencePdf = (formData) => {
  setMultipart(true);
  return client.post("/admin/mock-test-creator/reference-pdfs/preview", formData);
};

const estimateMockTestCost = (params) => {
  return client.get("/admin/mock-test-creator/estimate-cost", { params });
};

const updateMockTestSlot = (jobId, slotId, data) => {
  setMultipart(false);
  return client.patch(
    `/admin/mock-test-creator/jobs/${jobId}/questions/${slotId}`,
    data
  );
};

const approveMockTestSlot = (jobId, slotId) => {
  setMultipart(false);
  return client.post(
    `/admin/mock-test-creator/jobs/${jobId}/questions/${slotId}/approve`
  );
};

const rejectMockTestSlot = (jobId, slotId) => {
  setMultipart(false);
  return client.post(
    `/admin/mock-test-creator/jobs/${jobId}/questions/${slotId}/reject`
  );
};

const regenerateMockTestSlot = (jobId, slotId) => {
  setMultipart(false);
  return client.post(
    `/admin/mock-test-creator/jobs/${jobId}/questions/${slotId}/regenerate`
  );
};

const publishMockTestJob = (jobId) => {
  setMultipart(false);
  return client.post(`/admin/mock-test-creator/jobs/${jobId}/publish`);
};

const repairMockTestPublish = (jobId) => {
  setMultipart(false);
  return client.post(`/admin/mock-test-creator/jobs/${jobId}/repair-publish`);
};

const deleteMockTestCreatorJob = (jobId) => {
  return client.delete(`/admin/mock-test-creator/jobs/${jobId}`);
};

export default {
  login,
  verifyTwoFactorLogin,
  getSecurityStatus,
  changePassword,
  setupTwoFactor,
  enableTwoFactor,
  disableTwoFactor,
  getUserRole,
  getAllTestSeries,
  getArrayByName,
  addArrayByName,
  getAllRewardConditions,
  addSeries,
  updateSeries,
  deleteSeries,
  setMultipart,
  uploadSeriesImages,
  getSeriesDetails,
  addTest,
  updateTest,
  deleteTest,
  getAllTests,
  getTestDetails,
  uploadImage,
  AddQuestion,
  getAllQuestions,
  getAllQuestionPivots,
  getQuestionDetails,
  runQuestionValidation,
  runQuestionValidationBatch,
  markValidationIssueResolved,
  getQuestionMappings,
  updateQuestion,
  deleteQuestion,
  changeQuesVerify,
  getAllFeeders,
  getAllValidators,
  addFeeder,
  updateFeederPassword,
  deleteFeeder,
  updateFeederAccount,
  getEarningStats,
  claimEarning,
  getClaimRequests,
  updateClaimRequest,
  addValidator,
  updateValidatorPassword,
  deleteValidator,
  updateValidatorAccount,
  getAllValidatorQuestions,
  getValidatorAccountStats,
  AddQuestionVarient,
  getQuesVarients,
  shiftQuestions,
  getTestQuestions,
  searchQuestions,
  testQuesMapping,
  removeQuestionMapping,
  addTestSection,
  removeTestSection,
  getMappedTests,
  addTestToSeries,
  updateTestsToSeries,
  getAllStories,
  deleteStory,
  toggleStoryStatus,
  addStory,
  getAdminStats,
  getUsers,
  getUserDetail,
  getEmployees,
  addEmployee,
  getEmployeeDetails,
  updateEmployee,
  toggleEmployeeStatus,
  deleteEmployee,
  getUserAssistantSessions,
  getUserWallet,
  getUserOrders,
  getUserOrdersDetails,
  getUserLoginActivity,
  updateUserWallet,
  createUserWallet,
  initRefund,
  updateUser,
  verifyExplanation,
  getAllStaticContents,
  addStaticContent,
  viewStaticContent,
  getSectionQuestion,
  editStaticContent,
  deleteStaticContent,
  getTestReport,
  getTestTracker,
  updateQuestionState,
  getinitializeTest,
  getTestSolutions,
  submitTest,
  getTopicWiseReport,
  getQuestionSolutionDetails,
  getAllContentManagers,
  getContentManagerStats,
  getContentManagerDetails,
  deleteContentManager,
  updateContentManagerPassword,
  addContentManager,
  updateContentManager,
  getAllContentManagerWorkTasks,
  updateContentManagerRole,
  addContentManagerWorkTask,
  updateWorkTaskStatus,
  getWorkTaskDetails,
  deleteWorkTask,
  updateWorkTask,
  getAllTickets,
  getTicketDetails,
  performTicketAction,
  updateTicketStatus,
  getAllClaimRequests,
  getClaimRequestStats,
  getClaimRequestDetails,
  updateClaimRequestStatus,
  getCMClaimHistory,
  getCMTasks,
  CMAcceptRejectTask,
  getCMClaimableTaskList,
  getCMDashboardStatus,
  requestCMNewClaim,
  getEarningDashboardDetails,
  getCMFandQ,
  addCMTicket,
  getCMTaskDetails,
  validateCMQuestionUnit,
  updateQuestionStatusLevelWise,
  getAllDropdowns,
  addArray,
  deleteArrayByName,
  updateArray,
  getAllTestConfigs,
  addTestConfig,
  editTestConfig,
  deleteTestConfig,
  getTestConfigDetails,
  updateStory,
  changeTrackerLanguage,
  getLineChartData,
  getLeadershipBoard,
  getAllTestTrackers,
  getAllExamsNotifications,
  getExamNotificationsDetails,
  getAllEntities,
  getControlBoardList,
  updateControlBoardStatus,
  updateControlBoardYears,
  addExamNotification,
  updateExamNotification,
  deleteExamNotification,
  updateExamNotificationStatus,
  addEntity,
  updateEntity,
  deleteEntity,
  fillEntityContent,
  updateEntityBanners,
  getEntityDetails,
  deleteEntityBanner,
  uploadAttachment,
  getChatForums,
  getChatForumByExam,
  createChatForum,
  getChatForumDetails,
  deleteChatForumHashtag,
  updateChatForumHashtagStatus,
  getAdminTransactionStats,
  getAdminUsersGrowth,
  getEntitiesLists,
  addListsEntity,
  updateListsEntity,
  deleteListsEntity,
  getExamsLists,
  addListsExam,
  updateListsExam,
  deleteListsExam,
  getMappingsSubjects,
  addMappingsSubject,
  getMappingsSubject,
  deleteMappingsSubject,
  updateMappingsSubject,
  getAllSubjectsNames,
  getAllCompetitions,
  addCompetition,
  getCompetitionDetails,
  updateCompetitionStatus,
  updateCompetition,
  getCompetitionParticipants,
  getCompetitionScheduledTransitions,
  getCompetitionProcessingStatus,
  reprocessCompetitionResults,
  getCompetitionLeaderboard,
  getUserWalletHistory,
  getUserActivities,
  getUserWalletIntegrity,
  getPaymentTimeline,
  resolveWalletIntegrity,
  nllbTranslation,
  indicTranslation,
  getStudyMaterials,
  addStudyMaterial,
  updateStudyMaterial,
  updateStudyMaterialStatus,
  deleteStudyMaterial,
  getSubjectTopics,
  addQuestionVarientAuto,
  updateUserWalletStatus,
  updateUserWalletBalance,
  sendVerificationEmail,
  getUserAvatarsList,
  getOffers,
  addOffer,
  updateOffer,
  getFaqs,
  getFaqStats,
  addFaq,
  updateFaq,
  getFaqDetails,
  deleteFaq,
  addQuestionToFaq,
  updateQuestionToFaq,
  deleteQuestionToFaq,
  reorderQuestions,
  getUserIssues,
  getUserIssueStats,
  getUserIssueDetails,
  performInternalIssueAction,
  updateUserIssue,
  getQuestionBankStats,
  getAllSubjects,
  addSubject,
  updateSubject,
  deleteSubject,
  getAllTopics,
  addTopic,
  updateTopic,
  deleteTopic,
  getAllCountries,
  getAllStates,
  getAllIssuesCategories,
  generateSelfHostedExplanation,
  generateAIMLExplanation,
  generateShortExplanation,
  getUsersNotifications,
  addUsersNotification,
  updateUsersNotification,
  deleteUsersNotification,
  sendUsersNotification,
  createMockTestCreatorJob,
  listMockTestCreatorJobs,
  getMockTestCreatorJob,
  previewMockReferenceTest,
  previewMockReferencePdf,
  estimateMockTestCost,
  updateMockTestSlot,
  approveMockTestSlot,
  rejectMockTestSlot,
  regenerateMockTestSlot,
  publishMockTestJob,
  repairMockTestPublish,
  deleteMockTestCreatorJob,
};
